import { Chip } from '@monorepo/shared/componentsV2/Chip';
import { CalculationErrorChip } from '@monorepo/shared/componentsV2/errorDisplays/CalculationErrorChip';
import { Tooltip } from '@monorepo/shared/componentsV2/Tooltip';
import { useHasOrgUpdatePermissionOrSuperAdmin } from '@monorepo/shared/hooks/permissions/useHasPermissions';
import { cardBorder } from '@monorepo/shared/styles/card';
import {
  AggregationWorkflowStepResponse,
  MathUnit,
  WorkflowStepOperationType,
  WorkflowStepResponse,
} from 'mapistry-shared';
import React, { ReactElement } from 'react';
import styled from 'styled-components';
import { DatasetChip } from '../shared/DatasetChip';
import { getStepDisplayInfo } from './stepUtils';
import { WorkflowStepActions } from './WorkflowStepActions';

const Container = styled.div<{ $isHighlighted: boolean }>`
  position: relative;
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  align-items: center;
  justify-content: space-between;
  max-width: 70rem;
  padding: 0.625rem;
  ${cardBorder}
  ${({ $isHighlighted, theme }) =>
    $isHighlighted ? `background-color: ${theme.colors.builderBlue08};` : ''}
`;

const InnerContainer = styled.div`
  display: flex;
  flex-shrink: 1;
  justify-content: right;
  overflow: hidden;
`;

const LabelAndIcon = styled.div`
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  align-items: center;
  margin-right: 1rem;
`;

const IconContainer = styled.div`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 2.2rem;
  height: 1.5rem;
  margin-right: 0.5rem;
  color: ${({ theme }) => theme.colors.gray666};
  background-color: ${({ theme }) => theme.colors.blue08};
  border-radius: 5px;

  & svg {
    height: 0.8rem;
  }
`;

const StyledCalculationErrorChip = styled(CalculationErrorChip)`
  margin-left: 0.5rem;
`;

const StyledChip = styled(Chip)`
  padding: 0.5rem;
  margin-left: 0.25rem;
  overflow: hidden;
`;

const PlusNMoreChip = styled(StyledChip)`
  flex-shrink: 0;
`;

const ActionButtonContainer = styled.div`
  flex-shrink: 0;
  padding-left: 0.5rem;

  button {
    height: 1.5rem;
  }
`;

function AggregationCardChips({
  step,
}: {
  step: AggregationWorkflowStepResponse;
}) {
  const MAX_CHIP_COUNT = 3;
  const { aggregationDefinitions } = step.operationConfig;
  const aggregationDefinitionNames = aggregationDefinitions.map(
    (ad) => ad.name,
  );
  return (
    <>
      {aggregationDefinitionNames.slice(0, MAX_CHIP_COUNT).map((chipLabel) => (
        <StyledChip color="tertiary" label={chipLabel} key={chipLabel} />
      ))}
      {aggregationDefinitionNames.length > MAX_CHIP_COUNT && (
        <PlusNMoreChip
          label={`+${aggregationDefinitionNames.length - MAX_CHIP_COUNT} more`}
          key="extra chips"
        />
      )}
    </>
  );
}

function getAdditionalInfo(step: WorkflowStepResponse): ReactElement {
  const { type } = step;
  switch (type) {
    case WorkflowStepOperationType.FORMULA: {
      const outputUnits = step.operationConfig.outputUnits
        ? ` (${MathUnit.getLabel(step.operationConfig.outputUnits)})`
        : '';
      const formulaLabel = `${step.operationConfig.name}${outputUnits}`;

      return (
        <Tooltip title={step.operationConfig.friendlyExpression}>
          <div>
            <StyledChip color="tertiary" label={formulaLabel} />
          </div>
        </Tooltip>
      );
    }
    case WorkflowStepOperationType.JOIN: {
      const { joinToDataset } = step.operationConfig;
      return (
        <DatasetChip name={joinToDataset.name} type={joinToDataset.type} />
      );
    }
    case WorkflowStepOperationType.AGGREGATION:
      return <AggregationCardChips step={step} />;
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(
        `Trying to render a Workflow step for an unknown step type ${exhaustiveCheck}`,
      );
    }
  }
}

type WithMenuOrNot =
  | {
      onDeleteSuccess?: (deletedStepId: string) => void;
      openModalForExistingStep: (step: WorkflowStepResponse) => void;
      openPreviewStepPane: (step: WorkflowStepResponse) => void;
      showMenu: true;
    }
  | {
      onDeleteSuccess?: never;
      openModalForExistingStep?: never;
      openPreviewStepPane?: never;
      showMenu: false;
    };

type WorkflowStepCardProps = {
  className?: string;
  organizationId: string;
  step: WorkflowStepResponse;
  isHighlighted?: boolean;
} & WithMenuOrNot;

export function WorkflowStepCard({
  className,
  onDeleteSuccess,
  openModalForExistingStep,
  openPreviewStepPane,
  organizationId,
  showMenu,
  step,
  isHighlighted = false,
}: WorkflowStepCardProps) {
  const { icon, title } = getStepDisplayInfo(step);
  const additionalInfo = getAdditionalInfo(step);

  const canEditOrgSettings =
    useHasOrgUpdatePermissionOrSuperAdmin(organizationId);

  return (
    <Container className={className} $isHighlighted={isHighlighted}>
      <LabelAndIcon>
        <IconContainer aria-hidden>{icon}</IconContainer>
        {title}
        {step.doesUseIncompleteData && <StyledCalculationErrorChip />}
      </LabelAndIcon>
      <InnerContainer>
        {additionalInfo}
        {showMenu && canEditOrgSettings && (
          <ActionButtonContainer>
            <WorkflowStepActions
              organizationId={organizationId}
              step={step}
              openModalForExistingStep={openModalForExistingStep}
              openPreviewStepPane={openPreviewStepPane}
              onDeleteSuccess={onDeleteSuccess}
            />
          </ActionButtonContainer>
        )}
      </InnerContainer>
    </Container>
  );
}
